<template>
    <div class="espAvatarUploader">
        <div v-if="clickable">
            <v-avatar
                v-bind="avatar"
                @click="click"
                :size="size"
                :tile="tile"
                :style="avatarBorderStyle"
                :class="avatarClass"
            >
                <v-img v-if="previewUrl && !status.loading" :src="previewUrl" :contain="contain">
                    <template v-slot:placeholder>
                        <v-row class="fill-height ma-0" align="center" justify="center">
                            <v-progress-circular indeterminate color="primary"/>
                        </v-row>
                    </template>
                </v-img>

                <video controls="controls" v-else-if="isVideo(url) && !status.loading">
                    <source :src="url">
                    Your browser does not support the video.
                </video>

                <slot v-if="!url && !status.loading" name="none">
                    <v-icon large>mdi-account_circle</v-icon>
                </slot>

                <slot v-if="status.loading || status.processing" name="loading" :status="status">
                    <v-progress-circular
                        indeterminate
                        :value="status.progress"
                        :size="32"
                        :width="2"
                        color="primary"
                    />
                </slot>
            </v-avatar>

            <input
                type="file"
                ref="file"
                name="file"
                @change="select($event.target.name, $event.target.files)"
                style="display: none"
                :accept="acceptedMimeType"
                capture="environment"
            />
            
            <!-- Camera Choice Dialog for Mobile -->
            <v-dialog v-model="showCameraChoiceDialog" max-width="300" persistent>
                <div class="camera-choice-dialog">
                    <v-card 
                        flat 
                        rounded="lg" 
                        :color="theme.backgroundColor"
                    >
                        <v-card-title class="pb-0 pt-1">
                            <v-toolbar flat :color="theme.backgroundColor" class="px-0 menu-toolbar">
                                <v-toolbar-title style="width: 100%;">
                                    <h3 class="px-sm-6 text-center" :style="{'color': theme.textColor + '!important'}">
                                        {{$t('app_task_choose_media')}}
                                    </h3>
                                </v-toolbar-title>
                            </v-toolbar>
                        </v-card-title>
                        
                        <v-card-text class="pa-3">
                            <div class="camera-input-container">
<!--                                <div class="content-wrapper">-->
<!--                                    &lt;!&ndash; Camera Icon &ndash;&gt;-->
<!--                                    <div class="camera-icon-container mb-6 mb-sm-8 mb-md-12">-->
<!--                                        <v-icon -->
<!--                                            class="camera-icon" -->
<!--                                            size="48"-->
<!--                                            :color="theme.textColor"-->
<!--                                            :style="{'border-color': theme.textColor + '!important'}"-->
<!--                                        >-->
<!--                                            mdi-camera-->
<!--                                        </v-icon>-->
<!--                                    </div>-->
<!--                                    -->
<!--                                    <div class="text-center mb-4" :style="{'color': theme.textColor}">-->
<!--                                        {{$t('chooseSourceMessage')}}-->
<!--                                    </div>-->
<!--                                </div>-->

                                <div class="text-center button-container">
                                    <v-btn
                                        @click="chooseCamera"
                                        large
                                        block
                                        depressed
                                        rounded
                                        :color="theme.btnColorPrimary"
                                        class="mb-3"
                                    >
                                        {{$t(chooseMediaText)}}
                                    </v-btn>
                                    
                                    <v-btn
                                        @click="chooseFile"
                                        large
                                        block
                                        depressed
                                        rounded
                                        :color="theme.btnColorPrimary"
                                        class="mb-3"
                                    >
                                        {{$t(chooseMediaFileText)}}
                                    </v-btn>
                                    
                                    <v-btn
                                        @click="showCameraChoiceDialog = false"
                                        large
                                        block
                                        depressed
                                        rounded
                                        dark
                                        :opacity="0.8"
                                    >
                                        {{$t('close')}}
                                    </v-btn>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </div>
            </v-dialog>
        </div>

        <div v-else>
            <v-avatar 
                v-bind="avatar" 
                :size="size" 
                :tile="tile"
                :style="avatarBorderStyle"
                :color="theme.backgroundColor"
            >
                <v-img v-if="previewUrl" :src="previewUrl" contain/>
                <video controls="controls" v-else-if="isVideo(url)">
                    <source :src="url">
                    Your browser does not support the video.
                </video>

                <slot v-else name="none">
                    <v-icon large>account_circle</v-icon>
                </slot>
            </v-avatar>
        </div>
    </div>
</template>

<script>
import {layoutMixin} from "../../utils/layout-mixin";
import {imageMixin} from "../../utils/image-mixin";
import EXIF from 'exif-js';

export default {
    mixins: [imageMixin, layoutMixin],
    props: {
        url: {},
        avatarClass: {
            type: String,
            default: null
        },
        contain: {
            type: Boolean,
            default: true
        },
        size: {
            type: String,
            default: null
        },
        request: {
            type: Function,
            required: true
        },
        rename: {
            type: Function,
            required: false,
            default: file => file.name
        },
        field: {
            type: String,
            required: false,
            default: 'file'
        },
        clickable: {
            type: Boolean,
            required: false,
            default: true
        },
        tile: {
            type: Boolean,
            required: false,
            default: false
        },
        maxSize: {
            type: Number,
            required: false,
            default: 10240
        },
        headers: {
            type: Object,
            required: false,
            default: () => ({})
        },
        avatar: {
            type: Object,
            required: false,
            default: () => ({})
        },
        allowedMimeType: {
            type: String,
            default: 'image.*'
        },
        cameraChoice: {
            type: Boolean,
            default: false
        },
        maxWidth: {
            type: Number,
            default: 1920
        },
        maxHeight: {
            type: Number,
            default: 1920
        },
        compressionQuality: {
            type: Number,
            default: 0.8
        },
    },

    data: () => ({
        status: {
            loading: false,
            progress: 0,
            exifData: null,
            processing: false
        },
        previewUrl: null,
        originalFile: null, // Store original file
        originalExifData: null, // Store extracted EXIF data
        showCameraChoiceDialog: false,
    }),
    watch: {},
    beforeDestroy() {
        // Clean up video preview URL to prevent memory leaks
        if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(this.previewUrl);
        }
    },
    computed: {
        acceptedMimeType() {
            if (this._.isString(this.allowedMimeType)) {
                let split = this.allowedMimeType.split('.');
                return split.join('/');
            }
            return '';
        },
        isMobile() {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        },
        avatarBorderStyle() {
            const baseStyle = {
                'cursor': 'pointer',
                'overflow': 'visible',
                // 'border': `1px solid ${this.theme.textColor}`,
                // 'border-radius': '12px !important',
            }
            
            // Only apply border-radius if not tile (tile means square avatar)
            // if (!this.previewUrl) {
            //     baseStyle['border'] = `1px solid ${this.theme.textColor}`
            //     baseStyle['border-radius'] = '12px !important'
            // }
            
            return baseStyle
        },
        chooseMediaText() {
            return this.isMimeTypeVideo(this.allowedMimeType) ? 'app_task_choose_video' : 'app_task_choose_photo';
        },
        chooseMediaFileText() {
            return this.isMimeTypeVideo(this.allowedMimeType) ? 'app_task_choose_video_file' : 'app_task_choose_photo_file';
        },
    },
    methods: {
        // Extract EXIF data from file using exif-js
        extractExifData(file) {
            return new Promise((resolve) => {
                EXIF.getData(file, function() {
                    const exifData = {
                        orientation: EXIF.getTag(this, "Orientation"),
                        make: EXIF.getTag(this, "Make"),
                        model: EXIF.getTag(this, "Model"),
                        dateTime: EXIF.getTag(this, "DateTime"),
                        gpsLatitude: EXIF.getTag(this, "GPSLatitude"),
                        gpsLongitude: EXIF.getTag(this, "GPSLongitude"),
                        gpsLatitudeRef: EXIF.getTag(this, "GPSLatitudeRef"),
                        gpsLongitudeRef: EXIF.getTag(this, "GPSLongitudeRef"),
                        allExifData: EXIF.getAllTags(this)
                    };
                    
                    
                    resolve(exifData);
                });
            });
        },

        // Compress and resize image for better performance
        async compressImage(file) {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const img = new Image();
                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        
                        // Calculate new dimensions while maintaining aspect ratio
                        let { width, height } = this.calculateDimensions(img.width, img.height);
                        
                        canvas.width = width;
                        canvas.height = height;
                        
                        // Draw resized image
                        ctx.drawImage(img, 0, 0, width, height);
                        
                        // Convert to blob with compression
                        canvas.toBlob((blob) => {
                            const compressedFile = new File([blob], file.name, {
                                type: file.type,
                                lastModified: file.lastModified
                            });
                            resolve(compressedFile);
                        }, file.type, this.compressionQuality);
                    };
                    img.src = e.target.result;
                };
                reader.readAsDataURL(file);
            });
        },

        // Calculate optimal dimensions maintaining aspect ratio
        calculateDimensions(originalWidth, originalHeight) {
            let width = originalWidth;
            let height = originalHeight;
            
            // Only resize if image is larger than max dimensions
            if (width > this.maxWidth || height > this.maxHeight) {
                const aspectRatio = width / height;
                
                if (width > height) {
                    width = Math.min(width, this.maxWidth);
                    height = width / aspectRatio;
                } else {
                    height = Math.min(height, this.maxHeight);
                    width = height * aspectRatio;
                }
            }
            
            return { width: Math.round(width), height: Math.round(height) };
        },

        // Create a corrected image file that strips EXIF orientation data
        async createCorrectedImageFile(originalFile) {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = async (e) => {
                    const img = new Image();
                    img.onload = async () => {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        
                        // Calculate optimal dimensions for performance
                        let { width, height } = this.calculateDimensions(img.width, img.height);
                        
                        canvas.width = width;
                        canvas.height = height;
                        
                        // Draw resized image (strips EXIF orientation and improves performance)
                        ctx.drawImage(img, 0, 0, width, height);
                        
                        // Convert to blob with compression
                        canvas.toBlob((blob) => {
                            const correctedFile = new File([blob], originalFile.name, {
                                type: originalFile.type,
                                lastModified: originalFile.lastModified
                            });
                            
                            resolve(correctedFile);
                        }, originalFile.type, this.compressionQuality);
                    };
                    img.src = e.target.result;
                };
                reader.readAsDataURL(originalFile);
            });
        },

        click() {
            if (!this.status.loading) {
                if (!this.url) {
                    this.choose();
                } else {
                    this.replace();
                }
            } else {
                this.$emit('cancel');
            }
        },
        choose() {
            // If cameraChoice is false and we're on mobile, show choice dialog
            if (this.cameraChoice && this.isMobile) {
                this.showCameraChoiceDialog = true;
            } else {
                this.triggerFileInput();
            }
        },
        triggerFileInput() {
            return this.$refs.file.click();
        },
        chooseCamera() {
            this.showCameraChoiceDialog = false;
            // Temporarily set capture attribute to environment for camera
            this.$refs.file.setAttribute('capture', 'environment');
            this.$refs.file.click();
            // Reset capture attribute
            this.$refs.file.removeAttribute('capture');
        },
        chooseFile() {
            this.showCameraChoiceDialog = false;
            // Ensure no capture attribute for file upload
            this.$refs.file.removeAttribute('capture');
            this.$refs.file.click();
        },
        replace() {
            if (this.$listeners.replace) {
                this.$emit('replace');
            } else {
                this.choose();
            }
        },

        async updatePreview( withUpload ) {
            if (this.previewUrl) {
                const image = new Image();
                image.src = this.previewUrl;

                // Wait for the image to load
                await new Promise((resolve) => {
                    image.onload = resolve;
                });

                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // Set canvas dimensions to match image
                canvas.width = image.width;
                canvas.height = image.height;

                // Draw image without rotation
                ctx.drawImage(image, 0, 0);

                // Update the preview
                this.previewUrl = canvas.toDataURL();
                
                if (withUpload){
                    // Upload the corrected file
                    this.upload(this.base64ToFile(this.previewUrl));
                }
            }
        },

        async select(field, files) {
            if (files.length > 0) {
                let selectedFile = files[0];

                // Check file size before processing
                if (selectedFile.size > this.maxSize * 1024) {
                    this.$emit('error-size');
                    return;
                }

                // Check file type before processing
                if (!this.isValidFileType(selectedFile)) {
                    this.$emit('error-type');
                    return;
                }

                try {
                    if (this.isImageFile(selectedFile)){
                        // Show processing state
                        this.status.processing = true;
                        
                        // Store original file for EXIF preservation
                        this.originalFile = selectedFile;
                        
                        // Extract EXIF data before any processing
                        this.originalExifData = await this.extractExifData(selectedFile);
                        this.status.exifData = this.originalExifData;
                        
                        // Compress and correct image for better performance
                        let fileToUpload = selectedFile;
                        let fileForPreview = selectedFile;
                        
                        // Always compress and correct the image data
                        if (this.originalExifData.orientation && this.originalExifData.orientation !== 1) {
                            // EXIF orientation issue - create corrected and compressed file
                            fileToUpload = await this.createCorrectedImageFile(selectedFile);
                            fileForPreview = selectedFile; // Use original for preview
                        } else {
                            // No EXIF issues - just compress for performance
                            fileToUpload = await this.compressImage(selectedFile);
                            fileForPreview = selectedFile; // Use original for preview
                        }
                        
                        // Hide processing state
                        this.status.processing = false;
                        
                        // Load the image for preview
                        const reader = new FileReader();

                        // Set up the onload event to update previewUrl
                        reader.onload = async (e) => {
                            this.previewUrl = e.target.result; // Store initial image for preview
                            await this.updatePreview(false); // Wait for the updatePreview to finish
                        };

                        // Start reading the file as a data URL
                        reader.readAsDataURL(fileForPreview);
                        
                        // Upload the corrected file (or original if no correction needed)
                        this.upload(fileToUpload);
                    }else{
                        this.previewUrl = null;//reset the preview
                        this.originalFile = null;
                        this.originalExifData = null;
                        this.status.exifData = null;
                        
                        // Handle video files or other non-image files
                        if (this.isVideoFile(selectedFile)) {
                            // For videos, create a preview URL for display
                            this.previewUrl = URL.createObjectURL(selectedFile);
                        }
                        
                        // Upload non-image files as-is (videos, etc.)
                        this.upload(selectedFile);
                    }
                } catch (error) {
                    console.error('Error processing image:', error);
                    this.status.processing = false; // Hide processing state on error
                    this.$emit('error-processing');
                }
            } else {
                this.$emit('error-empty');
            }
        },


        isImageFile(file) {
            if (!file || !file.type.startsWith('image/')) {
                return false;
            }

            // Get the file extension
            const fileExtension = file.name.split('.').pop().toLowerCase();

            // Allowed extensions: jpg, jpeg, png, gif
            const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif'];

            // Check if the file extension is in the allowed list
            return allowedExtensions.includes(fileExtension);
        },

        isVideoFile(file) {
            if (!file || !file.type.startsWith('video/')) {
                return false;
            }

            // Get the file extension
            const fileExtension = file.name.split('.').pop().toLowerCase();

            // Allowed extensions: mp4, mov, avi, mpg, mpeg
            const allowedExtensions = ['mp4', 'mov', 'avi', 'mpg', 'mpeg'];

            // Check if the file extension is in the allowed list
            return allowedExtensions.includes(fileExtension);
        },

        isValidFileType(file) {
            // Check if file type matches allowedMimeType
            if (this.allowedMimeType === 'image.*') {
                return this.isImageFile(file);
            } else if (this.allowedMimeType === 'video.*') {
                return this.isVideoFile(file);
            } else if (this.allowedMimeType === 'image.*,video.*') {
                return this.isImageFile(file) || this.isVideoFile(file);
            }
            
            // Default: allow all files if no specific type is set
            return true;
        },

        createFileFromDataUrl(dataUrl) {
            return new Promise((resolve) => {
                const arr = dataUrl.split(',');
                const mime = arr[0].match(/:(.*?);/)[1];
                const bstr = atob(arr[1]);
                let n = bstr.length;
                const u8arr = new Uint8Array(n);
                while (n--) {
                    u8arr[n] = bstr.charCodeAt(n);
                }
                resolve(new Blob([u8arr], { type: mime }));
            });
        },
        // Function to generate a random file name
        generateRandomFileName(extension) {
            const randomString = Math.random().toString(36).substring(2, 15) +
                Math.random().toString(36).substring(2, 15);
            return `${randomString}.${extension}`;
        },

        // Function to map MIME type to file extension (only allowing jpg, jpeg, png, gif)
        getExtensionFromMimeType(mimeType) {
            const mimeMap = {
                'image/jpeg': 'jpg',  // Can map to 'jpg' or 'jpeg'
                'image/png': 'png',
                'image/gif': 'gif'
            };

            return mimeMap[mimeType] || null;  // Return null if the MIME type is not allowed
        },

        // Function to convert Base64 to File with a random file name
        base64ToFile(base64String) {
            // Split the base64 string into the content type and actual base64 content
            const arr = base64String.split(',');

            // Extract the MIME type
            const mimeType = arr[0].match(/:(.*?);/)[1];

            // Check if the MIME type is valid
            const extension = this.getExtensionFromMimeType(mimeType);

            if (!extension) {
                throw new Error('Unsupported file type. Only jpg, jpeg, png, and gif are allowed.');
            }

            // Decode base64 to binary string
            const bstr = atob(arr[1]);

            // Create a byte array to store binary data
            let n = bstr.length;
            const u8arr = new Uint8Array(n);

            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }

            // Generate a random file name
            const fileName = this.generateRandomFileName(extension);

            // Create a Blob and File object with the random name
            return new File([u8arr], fileName, { type: mimeType });
        },

        async upload(file) {
            this.$emit('loading', true);
            this.status.loading = true;

            const form = new FormData();
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    ...this.headers
                },
                onUploadProgress: (event) => {
                    if (event.lengthComputable) {
                        const progress = (event.loaded / event.total) * 100;
                        this.status.progress = progress;
                        this.$emit('progress', progress);
                    }
                }
            };

            form.append(this.field, file, this.rename(file));

            try {
                const upload = await this.request(form, config);
                // Emit success with both upload result and EXIF data
                this.$emit('success', upload, this.status.exifData);
            } catch (e) {
                this.$emit('failed', e);
            } finally {
                this.status.loading = false;
                this.status.progress = 0;
                this.$emit('loading', false);
            }
        },
        assignPseudoElementStyle() {
            document.documentElement.style.setProperty('--border-color', this.theme.textColor);
        }
    },
    mounted() {
        this.assignPseudoElementStyle();
    }
};
</script>

<style lang="scss">
.espAvatarUploader video {
    width: 100% !important;
    height: auto !important;
}

.espAvatarUploader{
    position: relative;
}

.camera-choice-dialog {
    .camera-icon-container {
        display: flex;
        justify-content: center;
        align-items: center;
        
        .camera-icon {
            border-radius: 50%;
            padding: 16px;
            border: 2px solid;
            width: 80px;
            height: 80px;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }
}
</style>
